import { useId } from 'react';
import type { ProviderItem, StateResponse } from '../lib/api.js';
import { getProviderSeverityTone, resolveProviderState } from '../lib/providerState.js';
import ProviderRow from './ProviderRow.js';
import CommandPanel from './ui/CommandPanel.js';
import { usePersistedPanelState } from '../hooks/usePersistedPanelState.js';
import SectionHeader from './ui/SectionHeader.js';

// ProviderGrid v0.5.1 — collapsible card. Title color bumped to bright white
// for legibility on the dark background; category badge removed because the
// title for Tier 2 IS the category, no need to repeat.
interface Props {
  title: string;
  providers: ProviderItem[];
  state: StateResponse['providers'];
  defaultExpanded?: boolean;
  storageKey: string;
}

export default function ProviderGrid({
  title,
  providers,
  state,
  defaultExpanded = false,
  storageKey,
}: Props) {
  const getState = (p: ProviderItem) => resolveProviderState(state, p);
  const [expanded, setExpanded] = usePersistedPanelState(storageKey, defaultExpanded);
  const issueCount = providers.filter((p) => getProviderSeverityTone(state, p) > 0).length;
  const contentId = useId();

  return (
    <CommandPanel>
      <SectionHeader
        title={
          <span className="flex items-center gap-2 min-w-0">
            <span className="text-dim">{providers.length} providers</span>
            <span className="truncate">{title}</span>
          </span>
        }
        right={
          issueCount > 0 ? (
            <span className="text-bad normal-case tracking-normal text-xs font-mono font-normal">
              {issueCount} issue{issueCount === 1 ? '' : 's'}
            </span>
          ) : null
        }
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        contentId={contentId}
      />
      {expanded && (
        <div id={contentId} className="divide-y divide-border/40">
          {providers.map((p) => (
            <ProviderRow
              key={p.slug}
              name={p.name}
              tier={p.tier}
              severity={getProviderSeverityTone(state, p)}
              sourceUrl={p.source_url}
              dashboardUrl={p.dashboard_url}
              message={getState(p)?.message}
              external={p.source_type === 'external'}
              description={p.description}
              reputationGrade={p.reputation_grade}
            />
          ))}
        </div>
      )}
    </CommandPanel>
  );
}
